import type { ContentType } from '@/interfaces';
import type { DBSchema, IDBPDatabase, IDBPObjectStore } from 'idb';
import { deleteDB, openDB } from 'idb';
import { getExpiryCutoff, getRetentionCutoff, isItemExpired } from './license';
import { reportKey } from './reportKey';
import type { OfflineItem } from './types';

const DB_NAME_PREFIX = 'bnef-offline-';
const DB_VERSION = 1;
const STORE = 'offline_items';

type ItemKey = [contentType: ContentType, itemId: string];

type StoredOfflineItem = OfflineItem & { reportKey?: string };

interface OfflineDB extends DBSchema {
    [STORE]: {
        key: ItemKey;
        value: StoredOfflineItem;
        indexes: {
            byValidatedAt: string;
            byReportKey: string;
        };
    };
}

type ItemStore<Mode extends IDBTransactionMode> = IDBPObjectStore<OfflineDB, [typeof STORE], typeof STORE, Mode>;

const dbName = (userId: number): string => `${DB_NAME_PREFIX}${userId}`;

const itemKey = (contentType: ContentType, itemId: string): ItemKey => [contentType, itemId];

const toOfflineItem = ({ reportKey: _reportKey, ...item }: StoredOfflineItem): OfflineItem => item;

function openOfflineDb(name: string, onClose: () => void = () => {}): Promise<IDBPDatabase<OfflineDB>> {
    return openDB<OfflineDB>(name, DB_VERSION, {
        upgrade(db, oldVersion) {
            if (oldVersion < 1) {
                const store = db.createObjectStore(STORE, { keyPath: ['contentType', 'itemId'] });
                store.createIndex('byValidatedAt', 'validatedAt');
                store.createIndex('byReportKey', 'reportKey');
            }
        },
        // Another tab wants to upgrade or delete the database: let it, and open a new connection on the next call.
        blocking(_currentVersion, _blockedVersion, event) {
            (event.target as IDBDatabase).close();
            onClose();
        },
        terminated: onClose,
    });
}

let connection: { userId: number; db: Promise<IDBPDatabase<OfflineDB>> } | null = null;

export function getDb(userId: number): Promise<IDBPDatabase<OfflineDB>> {
    if (connection?.userId !== userId) {
        void connection?.db.then((previous) => previous.close()).catch(() => {});
        const forget = () => {
            if (connection?.db === db) connection = null;
        };
        const db = openOfflineDb(dbName(userId), forget).catch((error: unknown) => {
            forget();
            throw error;
        });
        connection = { userId, db };
    }
    return connection.db;
}

async function getOfflineDbNames(): Promise<string[]> {
    if (typeof indexedDB.databases !== 'function') return [];
    const databases = await indexedDB.databases();
    return databases.flatMap(({ name }) => (name?.startsWith(DB_NAME_PREFIX) ? [name] : []));
}

async function inTransaction<T, Mode extends IDBTransactionMode>(
    db: IDBPDatabase<OfflineDB> | Promise<IDBPDatabase<OfflineDB>>,
    mode: Mode,
    run: (items: ItemStore<Mode>) => Promise<T>
): Promise<T> {
    const tx = (await db).transaction(STORE, mode);
    const [result] = await Promise.all([run(tx.store), tx.done]);
    return result;
}

export function saveItemToDb(userId: number, item: OfflineItem): Promise<void> {
    const key = reportKey(item.viewerUrl);
    return inTransaction(getDb(userId), 'readwrite', async (items) => {
        await items.put({ ...item, ...(key && { reportKey: key }) });
    });
}

/**
 * The user's readable item (it has a PDF and has not expired) whose `viewerUrl` has the same report key as `path`, e.g.
 * the URL the user opened offline.
 */
export async function getReadableItemByPathFromDb(userId: number, path: string): Promise<OfflineItem | undefined> {
    const key = reportKey(path);
    if (!key) return undefined;
    return inTransaction(getDb(userId), 'readonly', async (items) => {
        const matches = await items.index('byReportKey').getAll(key);
        const item = matches.find(({ pdf, validatedAt }) => pdf && !isItemExpired(validatedAt));
        return item && toOfflineItem(item);
    });
}

export function getItemFromDb(
    userId: number,
    contentType: ContentType,
    itemId: string
): Promise<OfflineItem | undefined> {
    return inTransaction(getDb(userId), 'readonly', async (items) => {
        const item = await items.get(itemKey(contentType, itemId));
        return item && toOfflineItem(item);
    });
}

/** Returns the user's items, newest first. Items past retention are left out. */
export function getAllItemsForUser(userId: number): Promise<OfflineItem[]> {
    return inTransaction(getDb(userId), 'readonly', async (items) => {
        const stored = await items.index('byValidatedAt').getAll(IDBKeyRange.lowerBound(getRetentionCutoff()));
        return stored.reverse().map(toOfflineItem);
    });
}

export function refreshItemExpiryInDb(userId: number, contentType: ContentType, itemId: string): Promise<void> {
    return inTransaction(getDb(userId), 'readwrite', async (items) => {
        const item = await items.get(itemKey(contentType, itemId));
        if (item?.pdf && !isItemExpired(item.validatedAt)) {
            await items.put({ ...item, validatedAt: new Date().toISOString() });
        }
    });
}

export function deleteItemFromDb(userId: number, contentType: ContentType, itemId: string): Promise<void> {
    return inTransaction(getDb(userId), 'readwrite', (items) => items.delete(itemKey(contentType, itemId)));
}

export async function clearAllItemsFromDb(): Promise<void> {
    await Promise.all((await getOfflineDbNames()).map((name) => deleteDB(name)));
}

export async function deleteExpiredItemsFromDb(): Promise<void> {
    await Promise.all(
        (await getOfflineDbNames()).map(async (name) => {
            const db = await openOfflineDb(name);
            try {
                await inTransaction(db, 'readwrite', async (items) => {
                    const expiredKeys = await items
                        .index('byValidatedAt')
                        .getAllKeys(IDBKeyRange.upperBound(getExpiryCutoff(), true));
                    await Promise.all(expiredKeys.map((key) => items.delete(key)));
                });
            } finally {
                db.close();
            }
        })
    );
}

/**
 * Deletes the databases of other users. In the user's database, drops the PDF of expired items and deletes the items
 * past retention.
 */
export async function cleanUpItemsForUserFromDb(userId: number): Promise<void> {
    const otherUsersDbs = (await getOfflineDbNames()).filter((name) => name !== dbName(userId));
    await Promise.all([
        ...otherUsersDbs.map((name) => deleteDB(name)),
        inTransaction(getDb(userId), 'readwrite', async (items) => {
            const byValidatedAt = items.index('byValidatedAt');
            const retentionCutoff = getRetentionCutoff();
            const [pastRetentionKeys, expiredItems] = await Promise.all([
                byValidatedAt.getAllKeys(IDBKeyRange.upperBound(retentionCutoff, true)),
                byValidatedAt.getAll(IDBKeyRange.bound(retentionCutoff, getExpiryCutoff(), false, true)),
            ]);
            await Promise.all([
                ...pastRetentionKeys.map((key) => items.delete(key)),
                ...expiredItems.filter((item) => item.pdf !== null).map((item) => items.put({ ...item, pdf: null })),
            ]);
        }),
    ]);
}
